import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import { useMutation } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

import { submitDraftVenues } from 'apis/merchant-onboarding-service/api';
import { APIErrorNotification } from 'common/components';
import CityRushWithBag from 'common/illustrations/CityRushWithBag';
import { Typography as T } from 'common/typography';

import DraftVenueListItem from './DraftVenueListItem';
import SubmitConfirmation from './SubmitConfirmation';
import ThankYouScreen from './ThankYouScreen';
import useDraftVenues from './useDraftVenues';
import { reportVenueSetupCompleted } from '../behavioral';

const Root = styled.div`
  max-width: 632px;
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: max(32px, 7vw) 16px 0;
  margin-inline-start: auto;
  margin-inline-end: auto;
`;

const StyledVenueIllustration = styled(CityRushWithBag)`
  display: block;
  width: 260px;
  margin-block-end: 16px;
`;

const Heading = styled(T.Heading2)`
  display: block;
  text-align: center;
  margin-block-end: 8px;
  color: ${cbSemanticCssVars.textPrimary};
`;

const Body = styled(T.Body2)`
  display: block;
  margin-block-end: 16px;
  color: ${cbSemanticCssVars.textPrimary};
  text-align: center;
`;

const List = styled.div`
  margin-block-end: 16px;
  width: calc(100% + 32px);
  @media (width >= 632px) {
    width: 100%;
  }
`;

const SubmitButton = styled(Button).attrs({
  variant: 'primary',
  size: 'large',
  stretch: true,
})``;

const SubmitError = styled(APIErrorNotification)`
  margin-block-start: 16px;
  width: 100%;
`;

const DraftVenueListPage = () => {
  const { url } = useRouteMatch();
  const { merchantId } = useParams<{ merchantId: string }>();
  const { data: draftVenues = [], error, refetch, isLoading: isLoadingDraftVenues } = useDraftVenues(merchantId);
  const [submitConfirmationOpen, setSubmitConfirmationOpen] = useState(false);

  const openSubmitConfirmation = () => {
    setSubmitConfirmationOpen(true);
  };

  const closeSubmitConfirmation = () => {
    setSubmitConfirmationOpen(false);
  };

  const {
    mutate: submit,
    isLoading: submitting,
    error: submitError,
  } = useMutation(['draft-venues/submit', merchantId], () => submitDraftVenues(merchantId), {
    onSuccess: async () => {
      // Await so that the submitting state remains true until refetch is done. This causes the view to
      // transition directly to the "Thank you" screen without showing a stale list screen in-between.
      await refetch();
    },
    onSettled: closeSubmitConfirmation,
  });

  const confirmSubmit = () => {
    submit();
    reportVenueSetupCompleted();
  };

  const sortedDraftVenues = useMemo(() => {
    const draftVenuesClone = [...draftVenues];
    return draftVenuesClone.sort((a, b) => (a.status < b.status ? -1 : 1));
  }, [draftVenues]);

  if (error) {
    return (
      <Root>
        <Alert variant="error">
          {error instanceof Error ? error.message : <FormattedMessage id="errors.unknown-error-retry-contact" />}
        </Alert>
      </Root>
    );
  }

  if (isLoadingDraftVenues) {
    return (
      <Root>
        <Spinner data-test-id="draft-venue-list-page-loading-indicator" size="large" />
      </Root>
    );
  }

  if (sortedDraftVenues.every(draftVenue => draftVenue.status === 'SUBMITTED_FOR_REVIEW')) {
    return (
      <Root>
        <ThankYouScreen />
      </Root>
    );
  }

  return (
    <>
      <Root>
        <StyledVenueIllustration />
        <Heading>
          <FormattedMessage id="venue-data-collection.start.heading" />
        </Heading>
        <Body>
          <FormattedMessage id="venue-data-collection.start.body" />
        </Body>
        {error ? (
          <Alert variant="error">
            {error instanceof Error ? error.message : <FormattedMessage id="errors.unknown-error-retry-contact" />}
          </Alert>
        ) : (
          <>
            <List>
              {sortedDraftVenues.map(draftVenue => (
                <DraftVenueListItem
                  key={draftVenue.id}
                  item={{
                    id: draftVenue.id,
                    name: draftVenue.name,
                    address: draftVenue.address,
                    url: `${url}/${draftVenue.id}`,
                    status: draftVenue.status,
                  }}
                />
              ))}
            </List>
            {sortedDraftVenues.every(draftVenue => draftVenue.status === 'READY_TO_SUBMIT') && (
              <SubmitButton data-test-id={'draft-venue-list-page-submit-button'} onClick={openSubmitConfirmation}>
                <FormattedMessage
                  id="venue-data-collection.start.submit"
                  values={{ count: sortedDraftVenues.length }}
                />
              </SubmitButton>
            )}
            {submitError && <SubmitError error={submitError} />}
          </>
        )}
      </Root>
      <SubmitConfirmation
        open={submitConfirmationOpen}
        onConfirm={confirmSubmit}
        onCancel={closeSubmitConfirmation}
        loading={submitting}
      />
    </>
  );
};

export default DraftVenueListPage;
